import { useMemo } from 'react'
import { useDashboardStore } from '../stores/dashboardStore'
import { useOrderEvents } from '../hooks/useOrderEvents'
import { LogFilterChips } from './LogFilterChips'
import { FILTER_CATEGORIES } from '../utils/logFilterCategories'
import { TimelineItem } from './TimelineItem'
import { SkeletonTimeline } from './SkeletonTimeline'

interface OrderTimelineProps {
  orderId: number
}

export function OrderTimeline({ orderId }: OrderTimelineProps) {
  const eventFilter = useDashboardStore((s) => s.eventFilter)
  const { data: allEvents = [], isLoading, isError, refetch } = useOrderEvents(orderId)

  const events = useMemo(() => {
    if (!eventFilter) return allEvents
    const allowedTypes = FILTER_CATEGORIES[eventFilter]
    if (!allowedTypes) return allEvents
    return allEvents.filter((e) => allowedTypes.includes(e.event_type))
  }, [allEvents, eventFilter])

  return (
    <>
      {/* Log header with filter chips */}
      <div className="flex items-center justify-between px-6 py-3 sticky top-0 bg-white z-10 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Hendelseslogg
        </span>
        <LogFilterChips />
      </div>

      {/* Timeline */}
      <div className="px-6 pb-12">
        {isLoading ? (
          <SkeletonTimeline />
        ) : isError ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-400 mb-2">Kunne ikke laste hendelser</p>
            <button onClick={() => refetch()} className="text-sm text-emerald-600 hover:underline">
              Prøv igjen
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">
            Ingen hendelser
          </div>
        ) : (
          <div className="relative pl-7 pt-4">
            {/* Vertical line */}
            <div className="absolute left-[9px] top-6 bottom-2 w-0.5 bg-gray-200" />

            {events.map((event) => (
              <TimelineItem key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
